import React, { useState, useEffect } from 'react';
import FormSectionRow from "../../../components/form/formSectionRow";
import Textarea from "../../../components/form/InputTextarea/textarea";
import Input from "../../../components/form/inputText/input";
import search from "../../../assets/icones/global/search.svg";
import InputAddress from "../../../components/form/inputText/inputAddress";

const __DEV__ = process.env.NODE_ENV === 'development';

const Access = ({ access, setActivity, isDisabled = false, isAdmin = true }) => {
    const [addressError, setAddressError] = useState(null);
    const [mapboxPreviewUrl, setMapboxPreviewUrl] = useState(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [coordinates, setCoordinates] = useState(() => {
        const initialCoords = {
            longitude: access.longitude?.value || '',
            latitude: access.latitude?.value || ''
        };
        return initialCoords;
    });
    
   
    // Synchroniser les coordonnées avec les props access quand elles changent
    useEffect(() => {
        const newCoordinates = {
            longitude: access.longitude?.value || '',
            latitude: access.latitude?.value || ''
        };
        
        // Mettre à jour seulement si les valeurs ont changé
        if (newCoordinates.longitude !== coordinates.longitude || 
            newCoordinates.latitude !== coordinates.latitude) {
            setCoordinates(newCoordinates);
        }
    }, [access.longitude?.value, access.latitude?.value]);

    useEffect(() => {
        // Utiliser l'URL Mapbox statique stockée dans les détails de l'activité
        // Récupérer depuis access ou depuis les props de l'activité
        const storedMapboxUrl = access.mapboxStaticUrl?.value;
        if (storedMapboxUrl) {
            setMapboxPreviewUrl(storedMapboxUrl);
        }
    }, [access.mapboxStaticUrl]);

    const handleChange = (event) => {
        event.preventDefault();
        const { name, value } = event.target;
        setActivity(`access.${name}.value`, value);
    };

    const handleCoordinateChange = (event) => {
        const { name, value } = event.target;
        const numericValue = parseFloat(value) || '';
                
        const newCoordinates = {
            ...coordinates,
            [name]: numericValue
        };
                
        setCoordinates(newCoordinates);
        setActivity(`access.${name}.value`, numericValue);
        
        // Si les deux coordonnées sont valides, générer une nouvelle URL Mapbox
        if (newCoordinates.longitude && newCoordinates.latitude) {
            updateMapboxPreviewFromCoordinates(newCoordinates.longitude, newCoordinates.latitude);
        }
    };

    // Fonction pour mettre à jour la prévisualisation Mapbox à partir de coordonnées
    const updateMapboxPreviewFromCoordinates = (longitude, latitude) => {
        try {
            // Validation des coordonnées
            if (!longitude || !latitude || 
                longitude < -180 || longitude > 180 || 
                latitude < -90 || latitude > 90) {
                console.warn('Coordonnées invalides:', { longitude, latitude });
                return;
            }

            // Générer l'URL Mapbox statique
            const mapboxStyleUrl = process.env.REACT_APP_MAPBOX_STYLE_URL || 'mapbox/streets-v11';
            const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;
            
            if (!mapboxToken) {
                console.error('Token Mapbox manquant');
                return;
            }

            const newMapboxUrl = `https://api.mapbox.com/styles/v1/${mapboxStyleUrl}/static/pin-l-marker+999(${longitude},${latitude})/${longitude},${latitude},14/600x400?access_token=${mapboxToken}`;
            
            
            // Mettre à jour la prévisualisation
            setIsLoadingPreview(true);
            setMapboxPreviewUrl(newMapboxUrl);
            setActivity(`access.mapboxStaticUrl.value`, newMapboxUrl);
            
        } catch (error) {
            console.error('Erreur lors de la génération de l\'URL Mapbox:', error);
        }
    };

    const handleChangeAddress = (name, addressDetails) => {
        try {            
            // Validation des données d'adresse
            if (!addressDetails.addressLine) {
                throw new Error('L\'adresse est requise');
            }

            // Mise à jour de l'adresse
            const newAddress = addressDetails.addressLine;
            if (access.location.value !== newAddress) {
                setActivity(`access.location.value`, newAddress);
            }

            // Si on a reçu des coordonnées du backend, les utiliser
            if (addressDetails.longitude && addressDetails.latitude) {

                
                const newCoordinates = {
                    longitude: addressDetails.longitude,
                    latitude: addressDetails.latitude
                };
                
                // Mettre à jour le state local immédiatement
                setCoordinates(newCoordinates);
                
                // Mettre à jour les props de l'activité
                setActivity(`access.longitude.value`, addressDetails.longitude);
                setActivity(`access.latitude.value`, addressDetails.latitude);
            }

            // Si on a reçu une URL Mapbox statique du backend, l'utiliser
            if (addressDetails.mapboxStaticUrl) {
                setIsLoadingPreview(true);
                setMapboxPreviewUrl(addressDetails.mapboxStaticUrl);
                // Sauvegarder l'URL Mapbox dans les détails de l'activité
                setActivity(`access.mapboxStaticUrl.value`, addressDetails.mapboxStaticUrl);
            }

            // Réinitialisation de l'erreur si tout est valide
            setAddressError(null);
        } catch (error) {
            console.error('Erreur lors de la mise à jour de l\'adresse:', error);
            setAddressError(error.message);
        }
    };

    const handleImageLoad = () => {
        setIsLoadingPreview(false);
    };

    const handleImageError = (e) => {
        console.error('Erreur de chargement de l\'image Mapbox');
        setIsLoadingPreview(false);
        e.target.style.display = 'none';
        e.target.nextSibling.style.display = 'block';
    };

    return (
        <FormSectionRow classContainer={"is__full"} isRequired={true}>
            <div className="form__container__col">
                <InputAddress
                    title={access.location.label}
                    name="location"
                    valueInput={access.location.value}
                    onChangeForm={handleChangeAddress}
                    errorEmpty={access.location.error}
                    errorMessage={addressError || access.location.errorMessage}
                    placeholder={access.location.placeholder}
                    icon={search}
                    validationType={"text"}
                    disabled={isDisabled}
                    isRequired={true}
                />
                
                {/* Section des coordonnées géographiques */}
                <div className="coordinates-section">
                    <h3 className="coordinates-section-title">
                        📍 Coordonnées géographiques
                    </h3>
                    <p className="coordinates-section-subtitle">
                        Coordonnées générées automatiquement ou modifiables manuellement
                    </p>
                    
                    <div className="coordinates-container">
                        <div className="coordinates-inputs">
                            <div className="coordinate-input">
                                <Input
                                    title="Longitude"
                                    name="longitude"
                                    valueInput={coordinates.longitude !== undefined ? coordinates.longitude : ''}
                                    onChangeForm={handleCoordinateChange}
                                    errorEmpty={access.longitude?.error}
                                    errorMessage={access.longitude?.errorMessage}
                                    placeholder="-122.4194"
                                    validationType="number"
                                    disabled={isDisabled}
                                    step="any"
                                />
                                <p className="coordinate-help-text">
                                    Longitude
                                </p>
                            </div>
                            <div className="coordinate-input">
                                <Input
                                    title="Latitude"
                                    name="latitude"
                                    valueInput={coordinates.latitude !== undefined ? coordinates.latitude : ''}
                                    onChangeForm={handleCoordinateChange}
                                    errorEmpty={access.latitude?.error}
                                    errorMessage={access.latitude?.errorMessage}
                                    placeholder="37.7749"
                                    validationType="number"
                                    disabled={isDisabled}
                                    step="any"
                                />
                                <p className="coordinate-help-text">
                                    Latitude
                                </p>
                            </div>
                        </div>
                        
                        {/* Prévisualisation de la carte Mapbox */}
                        <div className="mapbox-preview">
                            <p className="mapbox-preview-title">🗺️ Prévisualisation sur la carte</p>
                            <div className="mapbox-preview-container">
                                {/* Skeleton de chargement */}
                                {isLoadingPreview && (
                                    <div className="mapbox-skeleton">
                                        <div className="mapbox-skeleton-content">
                                            <div className="mapbox-skeleton-icon">🗺️</div>
                                            <p>Chargement de la prévisualisation...</p>
                                        </div>
                                    </div>
                                )}

                                {/* Image Mapbox */}
                                {mapboxPreviewUrl && (
                                    <>
                                        <img 
                                            src={mapboxPreviewUrl} 
                                            alt="Prévisualisation de la localisation"
                                            className="mapbox-preview-image"
                                            onLoad={handleImageLoad}
                                            onError={handleImageError}
                                            style={{ display: isLoadingPreview ? 'none' : 'block' }}
                                        />           
                                    </>
                                )}
                                
                                {/* Message si pas de coordonnées */}
                                {!mapboxPreviewUrl && !isLoadingPreview && (
                                    <div className="mapbox-placeholder">
                                        <div className="mapbox-placeholder-content">
                                            <div className="mapbox-placeholder-icon">📍</div>
                                            <p>Sélectionnez une adresse pour voir la prévisualisation</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            { __DEV__ && mapboxPreviewUrl && !isLoadingPreview && (
                                <p className="mapbox-preview-info">
                                    Image générée et mise en cache côté serveur
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            <Textarea
                title={access.accessDetails.label}
                name="accessDetails"
                value={access.accessDetails.value}
                onChange={handleChange}
                errorEmpty={access.accessDetails.error}
                errorMessage={access.accessDetails.errorMessage}
                placeholder={access.accessDetails.placeholder}
                isDisabled={isDisabled}
            />
        </FormSectionRow>
    );
};

export default Access;
